// What the Visual Mode install prompt should say, decided from what this browser
// has done. Pure: no DOM, no imports, storage and clock are passed in, so
// `npm run test:js` can run it without a browser.
//
// A store click is remembered per browser because an install belongs to one
// browser profile. It expires after a day: a click from last week says nothing
// about whether the user is mid-install now.

export const STORE_CLICK_KEY = 'aaa_extension_store_clicked_at';
export const STORE_CLICK_TTL_MS = 24 * 60 * 60 * 1000;

export function readStoreClick(storage, now) {
    let raw;
    try {
        raw = storage?.getItem(STORE_CLICK_KEY);
    } catch {
        // Storage blocked (privacy settings, sandboxed frame): behave as if the
        // user never clicked, which shows the plain install prompt.
        return null;
    }

    if (raw === null || raw === undefined) {
        return null;
    }

    const clickedAt = Number(raw);
    if (!Number.isFinite(clickedAt) || clickedAt > now) {
        return null;
    }

    if (now - clickedAt >= STORE_CLICK_TTL_MS) {
        clearStoreClick(storage);
        return null;
    }

    return clickedAt;
}

export function recordStoreClick(storage, now) {
    try {
        storage?.setItem(STORE_CLICK_KEY, String(now));
    } catch {
        // Storage blocked: the prompt falls back to the plain install state.
    }
}

// A `storage` event from another tab. Only a new click moves this tab: a removal
// means another tab detected the extension (or the record expired there), and
// Chrome and Edge never reach this tab without a reload, so dropping the click
// would put it back on "we can't find the extension" right after an install.
export function storeClickFromOtherTab(current, newValue, now) {
    if (newValue === null || newValue === undefined) {
        return current;
    }

    const clickedAt = Number(newValue);
    if (!Number.isFinite(clickedAt) || clickedAt > now || now - clickedAt >= STORE_CLICK_TTL_MS) {
        return current;
    }

    return clickedAt;
}

export function clearStoreClick(storage) {
    try {
        storage?.removeItem(STORE_CLICK_KEY);
    } catch {
        // Storage blocked: nothing was stored, so there is nothing to clear.
    }
}

/**
 * - ready:        the extension announced itself on this page.
 * - install:      no store click recorded in this browser.
 * - installing:   the store was opened after this page loaded, so this tab only
 *                 hears from the extension if the browser runs new extensions in
 *                 open tabs (Firefox does; Chrome and Edge do not) or the user
 *                 reloads; or detection on this page has not finished.
 * - troubleshoot: the store was opened before this page loaded and detection
 *                 finished without finding the extension, so a reload alone has
 *                 not helped: the user checks the extension's settings, then
 *                 reloads.
 */
export function promptVariant({ enabled, storeClickedAt, pageLoadedAt, detectionSettled }) {
    if (enabled) {
        return 'ready';
    }

    if (storeClickedAt === null || storeClickedAt === undefined) {
        return 'install';
    }

    if (storeClickedAt >= pageLoadedAt || !detectionSettled) {
        return 'installing';
    }

    return 'troubleshoot';
}
